import {
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { toast } from 'sonner';
import { useMapPanelContext } from './MapPanelContext';
import { ACTIVETAB } from './RightSideComponents';
import { useSelectionState } from '../../../components/SelectionStateProvider';

// --- Configuration ---

/** Debounce delay to avoid flicker during sidebar's open/close transition */
const RESIZE_DEBOUNCE_MS = 150;

/**
 * Minimum breathing room (px) required between left and right content
 * when both panels are open. Below this, we start making space.
 * Must be high enough that after step 1 frees ~144px (sidebar collapse),
 * steps 2/3 can still trigger if the viewport is still too cramped.
 */
const MIN_BUFFER_BOTH_PANELS = 200;

/**
 * Minimum breathing room (px) when only the target card is visible.
 * Higher than both-panels because the expanded target (464px) dominates
 * narrow viewports and the map needs to stay usable.
 */
const MIN_BUFFER_TARGET_ONLY = 250;

/**
 * Minimum breathing room (px) when only the sidebar is visible.
 * Very low threshold since without a target card there's plenty of room —
 * only close the panel when space is truly exhausted.
 */
const MIN_BUFFER_SIDEBAR_ONLY = 12;

const TOAST_ID = 'responsive-panel-closed';
const TOAST_DURATION_MS = 3000;

/** The 1fr column sits at index 1 in our grid template */
const FLEX_COLUMN_INDEX = 1;

// --- Types ---

interface UseResponsivePanelsInput {
  containerRef: React.RefObject<HTMLDivElement | null>;
  isTargetExpanded: boolean;
  setIsTargetExpanded: Dispatch<SetStateAction<boolean>>;
}

/**
 * Automatically manages panel visibility when viewport space runs out.
 *
 * HOW IT WORKS:
 * The grid's 1fr column acts as a natural "buffer zone" between left
 * content (search bar, metadata) and right panels (target card).
 * We read its resolved pixel width via getComputedStyle — when it
 * shrinks below a threshold, panels are about to collide.
 *
 * WHAT IT DOES (in order of gentleness):
 * 1. Collapses the left sidebar (icons-only mode, frees ~144px)
 * 2. Collapses the expanded target card (panels stay visible, just shorter)
 * 3. Closes the right panel entirely (shows a toast so the user knows why)
 *
 * Each step only fires when the previous, gentler option isn't available.
 */
export function useResponsivePanels({
  containerRef,
  isTargetExpanded,
  setIsTargetExpanded: rawSetIsTargetExpanded,
}: UseResponsivePanelsInput): {
  setIsTargetExpanded: Dispatch<SetStateAction<boolean>>;
} {
  const {
    activeTab,
    navigateToTab,
    reportDetailsExpanded,
    sidebarExpanded,
    setSidebarExpanded,
  } = useMapPanelContext();
  const { targetInfoDetailsList } = useSelectionState();

  const [bufferWidth, setBufferWidth] = useState<number>(Infinity);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Grace period after collapsing the sidebar. The CSS transition takes time
   * to complete, so the buffer measurement is stale until the layout settles.
   * Without this, step 3 (close panel) fires before step 1 has freed space.
   */
  const [sidebarCooldown, setSidebarCooldown] = useState(false);

  /** Same grace period after collapsing the target card (step 2 → step 3). */
  const [targetCooldown, setTargetCooldown] = useState(false);

  // --- Measure the grid's flexible buffer column ---
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const gridEl = container.firstElementChild as HTMLElement | null;
    if (!gridEl) return;

    const measure = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const cols = getComputedStyle(gridEl).gridTemplateColumns.split(' ');
        setBufferWidth(parseFloat(cols[FLEX_COLUMN_INDEX]) || 0);
      }, RESIZE_DEBOUNCE_MS);
    };

    // Measure immediately on mount
    const cols = getComputedStyle(gridEl).gridTemplateColumns.split(' ');
    setBufferWidth(parseFloat(cols[FLEX_COLUMN_INDEX]) || 0);

    const observer = new ResizeObserver(measure);
    observer.observe(gridEl);

    return () => {
      observer.disconnect();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [containerRef]);

  // --- What's currently visible? ---
  const isRightPanelOpen =
    activeTab !== ACTIVETAB.NONE &&
    activeTab !== ACTIVETAB.REPLAY &&
    reportDetailsExpanded;

  const isTargetVisible = targetInfoDetailsList.length > 0;

  // --- Step 1: Should we collapse the left sidebar? (gentlest) ---
  // The sidebar goes from w-52 (208px) to w-16 (64px), freeing 144px
  // without hiding any functionality — icons remain visible.
  const shouldCollapseSidebar =
    sidebarExpanded &&
    (isRightPanelOpen || isTargetVisible) &&
    (isRightPanelOpen
      ? bufferWidth < MIN_BUFFER_BOTH_PANELS
      : bufferWidth < MIN_BUFFER_TARGET_ONLY);

  // --- Step 2: Should we collapse the target card? ---
  const shouldCollapseTarget =
    isTargetExpanded &&
    isTargetVisible &&
    !shouldCollapseSidebar &&
    (isRightPanelOpen
      ? bufferWidth < MIN_BUFFER_BOTH_PANELS
      : bufferWidth < MIN_BUFFER_TARGET_ONLY);

  // --- Step 3: Should we close the right panel? ---
  const shouldCloseRightPanel =
    isRightPanelOpen &&
    !shouldCollapseSidebar &&
    (isTargetVisible
      ? !isTargetExpanded && bufferWidth < MIN_BUFFER_BOTH_PANELS
      : bufferWidth < MIN_BUFFER_SIDEBAR_ONLY);

  // --- Act: collapse left sidebar (step 1) ---
  useEffect(() => {
    if (!shouldCollapseSidebar) return;
    setSidebarExpanded(false);
    setSidebarCooldown(true);
    const timer = setTimeout(() => {
      setSidebarCooldown(false);
    }, 400); // Allow CSS transition + remeasure to settle
    return () => clearTimeout(timer);
  }, [shouldCollapseSidebar, setSidebarExpanded]);

  // --- Act: collapse target (step 2) ---
  useEffect(() => {
    if (!shouldCollapseTarget) return;
    if (sidebarCooldown) return;
    rawSetIsTargetExpanded(false);
    setTargetCooldown(true);
    const timer = setTimeout(() => {
      setTargetCooldown(false);
    }, 400);
    return () => clearTimeout(timer);
  }, [shouldCollapseTarget, rawSetIsTargetExpanded, sidebarCooldown]);

  // --- Act: close right panel (step 3) ---
  useEffect(() => {
    if (!shouldCloseRightPanel) return;
    if (sidebarCooldown || targetCooldown) return;
    navigateToTab(ACTIVETAB.NONE);
    toast.info('Panel closed to make room', {
      id: TOAST_ID,
      duration: TOAST_DURATION_MS,
    });
  }, [shouldCloseRightPanel, navigateToTab, sidebarCooldown, targetCooldown]);

  return { setIsTargetExpanded: rawSetIsTargetExpanded };
}
